import React from "react";
import { useHistory, useLocation } from "react-router";
import Layout from "../components/Layout";
import NavHeader from "../components/NavHeader";
import PropertyGrid from "../sections/PropertyGrid";
import { PropertySearchFilters, usePropertySearch } from "../swr/properties";
import {
  parsePropertySearch,
  PropertySearchState,
  stringifyPropertySearch,
} from "../utils/propertySearchParams";

const PropertyGridPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  // Filters and paging live in the URL so searches can be shared, bookmarked
  // and navigated with the back button.
  const { filters, page, pageSize, unsupportedState } = React.useMemo(
    () => parsePropertySearch(location.search),
    [location.search],
  );

  const { properties, pagination, isLoading, isError } = usePropertySearch(
    filters,
    page,
    pageSize,
  );

  // Keep the last known page count while the next page loads.
  const lastPagination = React.useRef(pagination);
  if (pagination) lastPagination.current = pagination;
  const totalPages = lastPagination.current?.totalPages ?? 0;
  const totalRecords = lastPagination.current?.totalRecords ?? 0;

  const navigate = (next: Omit<PropertySearchState, "unsupportedState">) =>
    history.push(`${location.pathname}${stringifyPropertySearch(next)}`);

  const handleFiltersChange = (nextFilters: PropertySearchFilters) =>
    navigate({ filters: nextFilters, page: 1, pageSize });

  const handlePageChange = (nextPage: number) => {
    navigate({ filters, page: nextPage, pageSize });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const handlePageSizeChange = (nextPageSize: number) =>
    navigate({ filters, page: 1, pageSize: nextPageSize });

  return (
    <Layout>
      <NavHeader pageTitle="Properties" routeName="Properties" />
      <PropertyGrid
        properties={unsupportedState ? [] : properties}
        loading={!unsupportedState && isLoading}
        error={!unsupportedState && isError}
        totalRecords={unsupportedState ? 0 : totalRecords}
        totalPages={unsupportedState ? 0 : totalPages}
        page={page}
        pageSize={pageSize}
        filters={filters}
        isFiltered={location.search.length > 1}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFiltersChange={handleFiltersChange}
      />
    </Layout>
  );
};

export default PropertyGridPage;
